import React, { useContext, useState } from "react";
import { Box, Divider, Link, Typography } from "@material-ui/core";
import { ActionLink, InformationalModal, useDeviceTypes } from "halifax";
import clsx from "clsx";
import "./styles.scss";
import {
  PAYMENT_SCHEDULE_SELECTED,
  RoomInfoProducts,
  VIEWED_PAYMENT_SCHEDULE_MODAL,
} from "redmond";
import { RouteComponentProps } from "react-router";
import { PaymentScheduleCardProps } from "./container";
import { HotelRoomPaymentScheduleModal } from "../../../shop/components/HotelRoomPaymentScheduleModal";
import {
  EDIT,
  PAY_LATER_LABEL,
  PAY_LATER_NOTES,
  PAY_NOW_LABEL,
  READ_TERMS_AND_CONDITIONS,
  READ_TERMS_AND_CONDITIONS_MODAL_CONTENT,
  READ_TERMS_AND_CONDITIONS_MODAL_CONTENT_MOBILE,
  READ_TERMS_AND_CONDITIONS_MODAL_TITLE,
  STEP_SUBTITLE,
  STEP_TITLE,
  STEP_TITLE_MOBILE,
} from "./textConstants";
import { AGENT_FEE } from "..";
import { ClientContext } from "../../../../App";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { RoomPaymentSchedule } from "redmond/hotels-module/interfaces";

export interface IPaymentScheduleCardProps
  extends PaymentScheduleCardProps,
    RouteComponentProps {
  disabled?: boolean;
}

export const PaymentScheduleCard = ({
  disabled,
  selectRoomType,
  chosenRoomInfoIndex,
  chosenRoomInfo,
  isPayLater,
  selectedAccount,
  ancillaries,
  schedulePriceQuote,
  history,
}: IPaymentScheduleCardProps) => {
  const { matchesMobile } = useDeviceTypes();

  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;
  const [modalRoomInfoProducts, setModalRoomInfoProducts] = useState<
    undefined | RoomInfoProducts
  >(undefined);

  const [showTermsAndConditionsModal, setShowTermsAndConditionsModal] =
    useState<boolean>(false);

  const openModal = () => {
    const paymentSchedules = new Set(
      chosenRoomInfo?.products.map(
        (p) => p.roomPaymentSchedule ?? RoomPaymentSchedule.PayNow
      )
    );
    trackEvent({
      eventName: VIEWED_PAYMENT_SCHEDULE_MODAL,
      properties: {
        payment_schedule_available: Array.from(paymentSchedules).sort().join(),
        stage: "checkout_page",
      },
    });
    setModalRoomInfoProducts(chosenRoomInfo as RoomInfoProducts);
  };

  const closeModal = () => {
    setModalRoomInfoProducts(undefined);
  };

  const renderPayLaterNotes = () => (
    <div className="paylater-notes">
      <span>{PAY_LATER_NOTES}</span>
    </div>
  );

  const renderReadTermsAndConditions = () => (
    <ActionLink
      content={
        <Typography className="terms-and-conditions" variant="body1">
          <span>{READ_TERMS_AND_CONDITIONS}</span>
        </Typography>
      }
      onClick={() => {
        setShowTermsAndConditionsModal(true);
      }}
    />
  );
  const renderPaymentSchedule = () => (
    <Box
      className={clsx("payment-schedule-container", {
        mobile: matchesMobile,
        disabled,
      })}
    >
      <Box className="title-container">
        <Typography className="step-title" variant="h2">
          {matchesMobile ? STEP_TITLE_MOBILE : STEP_TITLE}
        </Typography>
        {!matchesMobile && renderReadTermsAndConditions()}
      </Box>

      <Typography variant="body2" className="payment-step-subtitle">
        {STEP_SUBTITLE}
      </Typography>

      {matchesMobile && renderReadTermsAndConditions()}

      <Divider className="divider" />
      <Box
        className={clsx("payment-schedule", {
          mobile: matchesMobile,
          disabled,
        })}
      >
        <Box>
          <Typography className="payment-schedule-label">
            {isPayLater
              ? PAY_LATER_LABEL(selectedAccount?.productDisplayName ?? "")
              : PAY_NOW_LABEL(selectedAccount?.productDisplayName ?? "")}
          </Typography>

          {isPayLater && !matchesMobile && renderPayLaterNotes()}
        </Box>

        <Link
          disabled={disabled}
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            openModal();
          }}
          className="edit-payment-schedule"
        >
          {EDIT}
        </Link>
      </Box>

      {isPayLater && matchesMobile && renderPayLaterNotes()}
    </Box>
  );

  return (
    <>
      {renderPaymentSchedule()}
      {modalRoomInfoProducts && (
        <HotelRoomPaymentScheduleModal
          openModal={modalRoomInfoProducts !== undefined}
          onClose={() => {
            closeModal();
          }}
          onClickContinue={(selectedProductIndex: number) => {
            trackEvent({
              eventName: PAYMENT_SCHEDULE_SELECTED,
              properties: {
                payment_schedule_selected:
                  modalRoomInfoProducts.products[selectedProductIndex]
                    .roomPaymentSchedule ?? RoomPaymentSchedule.PayNow,
                stage: "checkout_page",
              },
            });
            selectRoomType(chosenRoomInfoIndex, selectedProductIndex);
            schedulePriceQuote({
              history,
              agentFee: isAgentPortal ? AGENT_FEE : 0,
              pollQuoteOnly: true,
              ancillaries,
            });

            closeModal();
          }}
          roomInfoProducts={modalRoomInfoProducts}
          isMobile={matchesMobile}
        />
      )}
      {!modalRoomInfoProducts && showTermsAndConditionsModal && (
        <InformationalModal
          header=""
          className="payment-schedule-terms-and-conditions-modal"
          title={READ_TERMS_AND_CONDITIONS_MODAL_TITLE}
          onClose={() => setShowTermsAndConditionsModal(false)}
          subtitle={
            matchesMobile
              ? READ_TERMS_AND_CONDITIONS_MODAL_CONTENT_MOBILE
              : READ_TERMS_AND_CONDITIONS_MODAL_CONTENT
          }
          openModal={showTermsAndConditionsModal}
          setOpenModal={setShowTermsAndConditionsModal}
          modalLinkCopy=""
          boldedModalLinkCopy=""
          isMobile={matchesMobile}
        />
      )}
    </>
  );
};
